import React, { useEffect, useState } from 'react'
import SubheaderInventoryAction from '../components/Inventory/SubheaderInventoryAction'
import { ContentArea } from '../components/Page/logix2020/ContentArea'
import { FlexWrapper } from '../components/Page/logix2020/FlexWrapper'
import BigFooter from '../components/Footer/BigFooter'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import SelectedAmounts from '../components/Inventory/InventoryAction/SelectedAmounts'
import { Alert, Button } from 'react-bootstrap'
import ChooseArticleSelectModal from '../components/Inventory/InventoryAction/ChooseArticleSelectModal'
import { MdLibraryAdd } from 'react-icons/md'
import TreeSelectModal from '../components/Inventory/InventoryAction/TreeSelectModal'
import ScanModal from '../components/Inventory/ScanModal'
import ModalComponent from '../components//Modals/ModalComponent'
import { t } from 'i18next'
import { ButtonWithLoadingState } from '../components/Buttons'
import ReturnContactDataModal from '../components/Inventory/ReturnContactDataModal'
import DeactivateModal from '../components/Employees/Form/FormParts/DeactivateModal'
import InventoryAssetReorderModal from '../components/Inventory/InventoryAssetReorderModal'
import { PdfButton } from '../components/Buttons/PdfButton'
import useReport from '../utils/hooks/useReport'
import { APIEndpoints } from '../utils/api/apiConfig'
import DownloadURLAlert from '../components/Forms/DownloadURLAlert'
import styled from 'styled-components'
import { BiBarcodeReader } from 'react-icons/bi'
import InventoryArticleScan from '../components/Inventory/InventoryArticleScan'
import InventoryArticleScanSwitch from '../components/Inventory/InventoryArticleScanSwitch'

export const StyledActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  button {
    margin-right: 10px;
  }

  @media screen and (max-width: 800px) {
    flex-direction: column;
    gap: 1rem;
    width: 100%;

    .btns {
      flex-direction: column;
      gap: 1rem;
    }
    button {
      margin-right: 0px;
    }
  }
`

const InventoryActionPage = inject(
  'inventoryStore',
  'uiStore'
)(
  observer((props) => {
    const { inventoryStore, uiStore } = props
    const { employeeId, actionType, targetId } = useParams()
    const [showAddArticlesModal, setShowAddArticlesModal] = useState(
      actionType !== 'assets'
    )
    const [showScanModal, setShowScanModal] = useState(false)
    const [showTreeModal, setShowTreeModal] = useState(false)
    const [showAskForReorder, setShowAskForReorder] = useState(false)
    const [showAskForReturn, setShowAskForReturn] = useState(false)
    const [openCompleteInventoryModal, setOpenCompleteInventoryModal] =
      useState(false)
    const [openMissingRFIDInventoryModal, setOpenMissingRFIDInventoryModal] =
      useState(false)

    const {
      createReport,
      reportIsLoading,
      reportErrors,
      reportDownloadUrls,
      setReportDownloadUrls,
    } = useReport()
    const navigate = useNavigate()
    const currentLocation = useLocation()
    useEffect(() => {
      const initData = async () => {
        const oldEmployeeId = inventoryStore.employeeId
        const oldActionType = inventoryStore.actionType
        inventoryStore.setIsAssetObject(false)
        inventoryStore.setActionType(actionType)
        inventoryStore.setTargetEmployeeId(targetId)
        await inventoryStore.setEmployeeId(employeeId)
        inventoryStore.loadAllInstances(true)
        inventoryStore.loadReturnReasons()
        uiStore.loadBPDataSettings()
        if (
          oldEmployeeId !== inventoryStore.employeeId ||
          oldActionType !== inventoryStore.actionType
        ) {
          await inventoryStore.loadEmployeeContingent(true)
        }
        if (inventoryStore.actionType === 'assets') {
          await inventoryStore.initAssetInventory()
        }
      }
      initData()
      return () => {
        inventoryStore.resetActionPage()
      }
    }, [actionType, employeeId, inventoryStore, targetId, uiStore])
    useEffect(() => {
      inventoryStore.checkScanModus()
    }, [inventoryStore.scanModus, inventoryStore])
    const hasPositions = (position) =>
      position.rows &&
      (position.rows.length ||
        (inventoryStore.actionType === 'assets' && !!position.currentQuantity))

    const contingentAmountsExist =
      inventoryStore.contingents &&
      inventoryStore.contingents.some((contingent) =>
        contingent.categories.some((category) =>
          category.articles.some((article) =>
            article.positions.some((position) => hasPositions(position))
          )
        )
      )
    const catalogAmountsExist =
      inventoryStore.editCatalogArticles?.length &&
      inventoryStore.editCatalogArticles.some((article) =>
        article.positions.some(
          (position) => position.rows && position.rows.length
        )
      )
    const confirmedZeroAmountIsMissing =
      inventoryStore.actionType === 'assets' &&
      inventoryStore.contingents &&
      inventoryStore.contingents.some((contingent) =>
        contingent.categories.some((category) =>
          category.articles.some((article) =>
            article.positions.some(
              (position) =>
                (position.rows?.length ?? 0) == 0 &&
                !position.zeroAmountIsConfirmed
            )
          )
        )
      )
    const checkNegativeAmountsExist = () => {
      return (
        inventoryStore.contingents &&
        inventoryStore.contingents.some((contingent) =>
          contingent.categories.some((category) =>
            category.articles.some((article) =>
              article.positions.some(
                (position) =>
                  !!position.currentQuantity &&
                  !!position.rows &&
                  position.currentQuantity > position.rows.length
              )
            )
          )
        )
      )
    }
    const checkOverstockExist = () => {
      return (
        inventoryStore.contingents &&
        inventoryStore.contingents.some((contingent) =>
          contingent.categories.some((category) =>
            category.articles.some((article) =>
              article.positions.some(
                (position) =>
                  !!position.currentQuantity &&
                  !!position.rows &&
                  ((!article.articleAvailability?.maxAmount &&
                    position.currentQuantity <
                      position.rows.length - position.openReturnsQuantity) ||
                    (!!article.articleAvailability?.maxAmount &&
                      article.articleAvailability?.maxAmount <
                        position.rows.length - position.openReturnsQuantity))
              )
            )
          )
        )
      )
    }
    const checkMissingRFIDsExist = () => {
      return (
        inventoryStore.contingents &&
        inventoryStore.contingents.some((contingent) =>
          contingent.categories.some((category) =>
            category.articles.some(
              (article) =>
                article.article.useRFIDs &&
                article.positions.some(
                  (position) =>
                    position.rows &&
                    (!position.rows.length ||
                      position.rows.some((row) => !row.instance?.rfid))
                )
            )
          )
        )
      )
    }
    const handleCheckBeforeSubmit = () => {
      if (inventoryStore.actionType === 'assets') {
        setOpenCompleteInventoryModal(true)
      } else if (
        inventoryStore.actionType === 'inventoryBookings' &&
        checkMissingRFIDsExist()
      ) {
        setOpenMissingRFIDInventoryModal(true)
      } else {
        handleOnSubmit()
      }
    }
    const handleOnSubmit = async () => {
      if (inventoryStore.actionType === 'assets' && contingentAmountsExist) {
        let waitForFurtherActions = false
        if (checkOverstockExist()) {
          setShowAskForReturn(true)
          waitForFurtherActions = true
        }
        if (
          inventoryStore.reorderForAssetsIsEnabled &&
          checkNegativeAmountsExist()
        ) {
          setShowAskForReorder(true)
          waitForFurtherActions = true
        }
        if (waitForFurtherActions) {
          return
        }
      }
      await inventoryStore.onSubmit(navigate, currentLocation)
      handlePrintInventoryPDF()
    }
    const handleCloseAssetReorderModal = async (submit) => {
      setShowAskForReturn(false)
      setShowAskForReorder(false)
      if (submit) {
        await inventoryStore.onSubmit(navigate, currentLocation)
        handlePrintInventoryPDF()
      }
    }
    const handlePrintInventoryPDF = () => {
      if (inventoryStore.actionType === 'assets') {
        createReport(
          'pdfReport',
          APIEndpoints.currentInventory(inventoryStore.employeeId, false),
          {}
        )
      }
    }
    const handleOpenDeactivateModal = (val) => {
      if (inventoryStore.actionType === 'assets') {
        inventoryStore.navigateToBasket(navigate)
        return
      } else {
        //inventoryStore.setOpenDeactivateModal(val)
        inventoryStore.checkInventoryExistsAskForDeactivation()
      }
    }
    const handleJumpToOrderAssets = () => {
      inventoryStore.setJumpToOrderAssets(true)
      setOpenCompleteInventoryModal(true)
    }
    return (
      <>
        <SubheaderInventoryAction />
        <FlexWrapper>
          <ContentArea>
            <div className='container-fluid'>
              <StyledActionHeader>
                <div className='d-flex justify-content-start btns'>
                  <Button
                    onClick={() => setShowAddArticlesModal(true)}
                    disabled={inventoryStore.scanModus}
                  >
                    <MdLibraryAdd className='me-3' />
                    {t('inventory:addArticle')}
                  </Button>
                  {inventoryStore.actionType === 'assets' &&
                    inventoryStore.reorderForAssetsIsEnabled && (
                      <Button
                        onClick={() => handleJumpToOrderAssets()}
                        disabled={inventoryStore.scanModus}
                      >
                        {t('inventory:contingentTemplate')}
                      </Button>
                    )}
                </div>
                <div className='d-flex justify-content-start btns'>
                  {(inventoryStore.actionType === 'assets' || inventoryStore.actionType === 'returnBookings') && (
                    <Button
                      onClick={() => inventoryStore.setOpenScanModal(true)}
                    >
                      <BiBarcodeReader className='me-3' />
                      {t('productsOverview:articleScan')}
                    </Button>
                  )}
                  {inventoryStore.actionType === 'assets' && (
                    <InventoryArticleScanSwitch />
                  )}
                </div>
                <div className='d-flex justify-content-end btns'>
                  <PdfButton
                    variant='secondary'
                    onClick={() =>
                      createReport(
                        'pdfReport',
                        APIEndpoints.currentInventory(
                          inventoryStore.employeeId,
                          false
                        ),
                        {}
                      )
                    }
                    isLoading={reportIsLoading.pdfReport}
                    buttonText={t('inventory:currentEquipment')}
                    disabled={inventoryStore.scanModus}
                  />
                  {inventoryStore.scanModus ? (
                    <ButtonWithLoadingState
                      disabled={
                        !(contingentAmountsExist || catalogAmountsExist)
                      }
                      buttonText={t('inventory:confirm')}
                      onClick={() => inventoryStore.onConfirmCompleteScan()}
                    />
                  ) : (
                    <ButtonWithLoadingState
                      disabled={
                        confirmedZeroAmountIsMissing ||
                        !(contingentAmountsExist || catalogAmountsExist) ||
                        inventoryStore.loadingSave ||
                        inventoryStore.submitSuccess
                      }
                      buttonText={t('submitButton')}
                      isLoading={inventoryStore.loadingSave}
                      onClick={() => handleCheckBeforeSubmit()}
                    />
                  )}
                </div>
              </StyledActionHeader>

              {reportDownloadUrls.pdfReport ? (
                <DownloadURLAlert
                  downloadUrl={
                    reportDownloadUrls.pdfReport || reportDownloadUrls
                  }
                  setDownloadUrl={() => setReportDownloadUrls({})}
                  fileName={t('inventory:currentEquipment')}
                />
              ) : null}
              {inventoryStore.submitSuccess ? (
                <Alert variant='success'>
                  {t('registrationBox:savingSuccess')}
                </Alert>
              ) : null}
              {inventoryStore.submitError ? (
                <Alert variant='danger'>{inventoryStore.submitError}</Alert>
              ) : null}
              {reportErrors.pdfReport ? (
                <Alert className='subheader__alert' variant='danger'>
                  {reportErrors?.pdfReport?.value}
                </Alert>
              ) : null}
              <SelectedAmounts />
            </div>
          </ContentArea>
        </FlexWrapper>
        {showAddArticlesModal && (
          <ChooseArticleSelectModal
            setShowScanModal={setShowScanModal}
            setShowTreeModal={setShowTreeModal}
            handleClose={() => setShowAddArticlesModal(false)}
          />
        )}
        {showScanModal && (
          <ScanModal
            handleClose={() => setShowScanModal(false)}
            actionType={actionType}
          />
        )}
        {showTreeModal && (
          <TreeSelectModal
            handleClose={() => setShowTreeModal(false)}
            actionType={actionType}
          />
        )}
        {inventoryStore.retourResponse && inventoryStore.retourResponse.id && (
          <ReturnContactDataModal
            retourResponse={inventoryStore.retourResponse}
            setReturnResponse={(val) => inventoryStore.setReturnResponse(val)}
            setOpenDeactivateModal={(val) => handleOpenDeactivateModal(val)}
            showReturndetails={inventoryStore.actionType === 'assets'}
          />
        )}
        {inventoryStore.openDeactivateModal ? (
          <DeactivateModal
            handleClose={() => {
              inventoryStore.setOpenDeactivateModal(false)
              navigate(currentLocation.state.from)
            }}
            employeeId={employeeId}
          />
        ) : null}
        {(showAskForReorder || showAskForReturn) && (
          <InventoryAssetReorderModal
            handleClose={(submit) => handleCloseAssetReorderModal(submit)}
            askForReorder={showAskForReorder}
            askForReturn={showAskForReturn}
            setReorder={(bool) => inventoryStore.setReorderMissingAmounts(bool)}
            setReturn={(bool) => inventoryStore.setAutomaticReturnAmounts(bool)}
          />
        )}
        {openCompleteInventoryModal && (
          <ModalComponent
            ModalTitle={t('inventory:assetCompleteInventoryTitle')}
            functionButton1={() => {
              setOpenCompleteInventoryModal(false)
              handleOnSubmit()
            }}
            textButton1={t('inventory:completeInventory')}
            variantButton1='danger'
            variantBody='alert-danger'
            BodyPart1={t('inventory:assetCompleteInventoryBody')}
            functionCancel={(submit) => {
              if (!submit) {
                inventoryStore.setJumpToOrderAssets(false)
              }
              setOpenCompleteInventoryModal(false)
            }}
          />
        )}
        {openMissingRFIDInventoryModal && (
          <ModalComponent
            ModalTitle={t('inventory:missingRFIDInventoryTitle')}
            functionButton1={() => {
              setOpenMissingRFIDInventoryModal(false)
              handleOnSubmit()
            }}
            textButton1={t('yes')}
            variantButton1='danger'
            variantBody='alert-danger'
            BodyPart1={t('inventory:missingRFIDInventoryBody')}
            functionCancel={(submit) => {
              setOpenMissingRFIDInventoryModal(false)
            }}
          />
        )}
        {inventoryStore.openScanModal && (
          <InventoryArticleScan
            handleClose={() => inventoryStore.setOpenScanModal(false)}
            employeeId={employeeId}
          />
        )}
        <BigFooter />
      </>
    )
  })
)

export default InventoryActionPage
